import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import JalaliDatePicker from "@/components/JalaliDatePicker";
import { formatJalaliDate } from "@/lib/format";
import { formatDecimal, formatNumber, persianDigits } from "@/features/reports/lib/format";
import { getPersianLabel } from "@/lib/persianUi";

import { useImports } from "../hooks/useImports";
import { ChartLegend } from "../components/chartTheme";
import { fa } from "../i18n/fa";
import "../traceability.css";

const SERIES = [
  { key: "registered", color: "var(--green)" },
  { key: "at_customs", color: "#F59E0B" },
  { key: "released", color: "#1E88E5" },
];

export default function ImportsPage() {
  const [product, setProduct] = useState("beef");
  const [granularity, setGranularity] = useState("week");
  const [originCountry, setOriginCountry] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const { data, loading, error } = useImports({
    product,
    granularity,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
    origin_country: originCountry || undefined,
  });

  const chartData = useMemo(
    () => data?.buckets.map((bucket) => ({
      ...bucket,
      registered: bucket.registered.value,
      at_customs: bucket.at_customs.value,
      released: bucket.released.value,
    })) ?? [],
    [data],
  );

  const unit = data?.unit === "head" ? fa.imports.head : fa.imports.kg;
  const renderValue = (value) => `${formatDecimal(value)} ${unit}`;

  return (
    <main className="trace tr-imports" dir="rtl">
      <div className="tr-top">
        <div className="tr-top-text">
          <p className="tr-eyebrow">{fa.page.eyebrow}</p>
          <h1>{fa.imports.title}</h1>
          <div className="tr-sub">{fa.imports.description}</div>
        </div>
        <div className="tr-import-filters">
          <label>
            <span>{fa.imports.product}</span>
            <select className="tr-select" value={product} onChange={(event) => setProduct(event.target.value)}>
              {(data?.products ?? ["beef", "lamb", "oil", "live_animals"]).map((item) => (
                <option key={item} value={item}>{getPersianLabel(fa.imports.products, item, 'traceability.importProducts')}</option>
              ))}
            </select>
          </label>
          <label>
            <span>{fa.imports.country}</span>
            <select className="tr-select" value={originCountry} onChange={(event) => setOriginCountry(event.target.value)}>
              <option value="">{fa.imports.allCountries}</option>
              {(data?.origin_countries ?? []).map((country) => (
                <option key={country} value={country}>{getPersianLabel(fa.originCountries, country, 'traceability.originCountries')}</option>
              ))}
            </select>
          </label>
          <label>
            <span>{fa.imports.granularity}</span>
            <select className="tr-select" value={granularity} onChange={(event) => setGranularity(event.target.value)}>
              <option value="week">{fa.imports.week}</option>
              <option value="month">{fa.imports.month}</option>
              <option value="year">{fa.imports.year}</option>
            </select>
          </label>
          <label>
            <span>{fa.imports.fromDate}</span>
            <JalaliDatePicker className="tr-select" value={fromDate} onChange={setFromDate} aria-label="از تاریخ شمسی" rangeStart={fromDate} rangeEnd={toDate} />
          </label>
          <label>
            <span>{fa.imports.toDate}</span>
            <JalaliDatePicker className="tr-select" value={toDate} onChange={setToDate} aria-label="تا تاریخ شمسی" rangeStart={fromDate} rangeEnd={toDate} />
          </label>
        </div>
      </div>

      {error && <div className="tr-card tr-error">{fa.imports.error}</div>}
      {loading && !data && <div className="tr-card tr-loading">{fa.page.loading}</div>}

      {data && (
        <>
          <section className="tr-card tr-panel tr-import-chart">
            <h2 className="tr-title">{fa.imports.chartTitle} <span className="tr-hint">({unit})</span></h2>
            {chartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-light)" />
                  <XAxis dataKey="label" tickFormatter={persianDigits} />
                  <YAxis tickFormatter={formatNumber} />
                  <Tooltip
                    labelFormatter={persianDigits}
                    formatter={(value, key) => [renderValue(value), getPersianLabel(fa.imports.statuses, key, 'traceability.importStatuses')]}
                  />
                  <ChartLegend />
                  {SERIES.map(({ key, color }) => (
                    <Bar key={key} dataKey={key} name={getPersianLabel(fa.imports.statuses, key, 'traceability.importStatuses')} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="tr-empty"><p>{fa.imports.noData}</p></div>
            )}
          </section>

          <section className="tr-card tr-panel tr-import-table-card">
            <h2 className="tr-title">{fa.imports.tableTitle}</h2>
            <div className="tr-import-table-wrap">
              <table className="tr-import-table">
                <thead>
                  <tr>
                    <th>{fa.imports.period}</th>
                    <th>{fa.imports.start}</th>
                    <th>{fa.imports.end}</th>
                    {SERIES.map(({ key }) => (
                      <th key={key} colSpan="2">{getPersianLabel(fa.imports.statuses, key, 'traceability.importStatuses')}</th>
                    ))}
                  </tr>
                  <tr>
                    <th colSpan="3" />
                    {SERIES.map(({ key }) => (
                      <FragmentHeaders key={key} amount={fa.imports.amount} growth={fa.imports.growth} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.buckets.map((bucket) => (
                    <tr key={bucket.start}>
                      <td>{persianDigits(bucket.label)}</td>
                      <td>{formatJalaliDate(bucket.start)}</td>
                      <td>{formatJalaliDate(bucket.end)}</td>
                      {SERIES.map(({ key }) => (
                        <td key={`${key}-value`}>{formatNumber(bucket[key].value)}</td>
                      )).flatMap((valueCell, index) => [
                        valueCell,
                        <td key={`${SERIES[index].key}-growth`}>
                          {bucket[SERIES[index].key].growth_pct === null
                            ? "—"
                            : `${formatDecimal(bucket[SERIES[index].key].growth_pct)}٪`}
                        </td>,
                      ])}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
}

function FragmentHeaders({ amount, growth }) {
  return (
    <>
      <th>{amount}</th>
      <th>{growth}</th>
    </>
  );
}
